import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useLocation } from "react-router";
import { ChecklistServicoDetalhe } from "../../domain/models/checklist_servico_detalhe";
import { ChecklistServicoAmbienteCadastroForm } from "../schemas/checklist_servico_ambiente_lista/checklistServicoAmbienteCadastro";
import { ChecklistServicoAmbienteCopiaForm } from "../schemas/checklist_servico_ambiente_lista/checklistServicoAmbienteCopia";
import { ChecklistServicoLiberacao } from "../../domain/models/checklist_servico_liberacao";
import { useChecklistServicoLiberacaoListaFilters } from "../hooks/ChecklistServicoLiberacao_lista/filters";
import { useChecklistServicoLiberacaoListaPagination } from "../hooks/ChecklistServicoLiberacao_lista/pagination";

type LoadChecklistServicoLiberacaoListaState = {
	idChecklistServico: number;
	descricao?: string,

	currentPage?: number;
	rowsPerPage?: number;
}

export const useChecklistServicoLiberacaoListaViewModel = () => {
	const {
		obras: {
			getChecklistServicoByIdUseCase,
			getAllChecklistServicoLiberacaoUseCase,
			getChecklistServicoLiberacaoByIdUseCase,
			createChecklistServicoLiberacaoUseCase,
			updateChecklistServicoLiberacaoUseCase,
			deleteChecklistServicoLiberacaoUseCase,
		},
		commons: {
			getAllLogAlteracaoUseCase,
			getAllLogAlteracaoByParentUseCase,
		}
	} = useDependencyInjection();

	const backOrNavigate = useBackOrNavigate();

	const { pushErrorNotification, pushInfoNotification } = usePageLoader();
	const debouncedRef = useCreateDebounceHandler(1500);

	const location = useLocation();

	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [openCopiaModal, setOpenCopiaModal] = useState(false);
	const [openCadastroModal, setOpenCadastroModal] = useState(false);

	const [selectedIdChecklistServicoLiberacao, setSelectedIdChecklistServicoLiberacao] = useState<number>();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const [errorMessage, setErrorMessage] = useState<string>();
	const [checklistServicoLiberacaoLista, setChecklistServicoLiberacaoLista] = useState<ChecklistServicoLiberacao[]>();
	const [selectedChecklistServicoLiberacao, setSelectedChecklistServicoLiberacao] = useState<ChecklistServicoLiberacao | undefined>();
	const [selectedChecklistServico, setSelectedChecklistServico] = useState<ChecklistServicoDetalhe | undefined>();

	const [selectedIdChecklistServico, setSelectedIdChecklistServico] = useState<number | undefined>();

	const filters = useChecklistServicoLiberacaoListaFilters();
	const pagination = useChecklistServicoLiberacaoListaPagination();

	const changeHistory = useChangeHistory({
		getAllLogAlteracaoUseCase,
		getAllLogAlteracaoByParentUseCase,
		onSetError: (e) => pushErrorNotification('Erro', [e]),
	})

	//#region CARREGAMENTO.

	const loadChecklistServicoLiberacaoLista = useCallback(async (fs?: LoadChecklistServicoLiberacaoListaState) => {
		try {
			setLoading(true);
			const filteredIdChecklistServicoFilter = fs?.idChecklistServico ?? selectedIdChecklistServico;
			const filteredDescricao = fs?.descricao ?? filters.descricao;

			const filteredPage = fs?.currentPage ?? pagination.page;
			const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

			await getAllChecklistServicoLiberacaoUseCase.execute(
				filteredIdChecklistServicoFilter ?? 0,
				filteredDescricao,
				filteredPage,
				filteredRowsPerPage,
			);

			setChecklistServicoLiberacaoLista(getAllChecklistServicoLiberacaoUseCase.items);
			pagination.setTotalItems(getAllChecklistServicoLiberacaoUseCase.totalItems);
		} catch (e: unknown) {
			setChecklistServicoLiberacaoLista([]);
			pushErrorNotification("Error", processError(e, "Falha ao buscar lista de checklist de servico!"));
		} finally {
			setLoading(false);
		}
	}, [
		selectedIdChecklistServico,
		filters,
		pagination,
		getAllChecklistServicoLiberacaoUseCase,
		pushErrorNotification,
	]);

	const loadChecklistServicoAmbienteById = useCallback(async (idChecklistServicoLiberacao: number) => {
		try {
			setLoading(true);
			const checklist = await getChecklistServicoLiberacaoByIdUseCase.execute(idChecklistServicoLiberacao);
			setSelectedChecklistServicoLiberacao(checklist);
		} catch (e: unknown) {
			pushErrorNotification('Error', processError(e, 'Falha ao buscar Ambiente do checklist de servico!'));
		} finally {
			setLoading(false);
		}
	}, [
		getChecklistServicoLiberacaoByIdUseCase,
		pushErrorNotification
	]);

	const loadChecklistServicoById = useCallback(async (idChecklistServico: number) => {
		try {
			setLoading(true);
			const checklistServico = await getChecklistServicoByIdUseCase.getChecklistServicoById(idChecklistServico);
			setSelectedChecklistServico(checklistServico);
		} catch (e: unknown) {
			pushErrorNotification('Error', processError(e, 'Falha ao buscar checklist de servico!'));
		} finally {
			setLoading(false);
		}
	}, [
		getChecklistServicoByIdUseCase,
		pushErrorNotification,
	]);

	//#endregion

	/**
	 * useEffect
	 */

	//#region PAGE START
	usePageStartBootstrap(async () => {
		try {
			const params = new URLSearchParams(location.search);
			const paramId = params.get('id');
			const locationId = paramId
				? parseInt(paramId, 10)
				: undefined;

			if (locationId !== undefined) {
				setSelectedIdChecklistServico(locationId);
				await loadChecklistServicoById(locationId);
				await loadChecklistServicoLiberacaoLista(
					locationId ? { idChecklistServico: locationId } : undefined
				);
			}
		} catch (e: unknown) {
			setErrorMessage(processError(e, "Falha ao buscar lista de checklist de servico de ambiente!")[0]);
		}
	});

	/**
	 * Eventos
	 */

	const handleBack = useCallback(() => {
		backOrNavigate(`/ChecklistServico_lista`);
	}, [
		backOrNavigate,
	]);

	//#endregion

	//#region NOVO/EDITAR.
	const handleChecklistServicoLiberacaoOpenState = useCallback(async (idChecklistServicoAmbiente: number | null, openState: boolean) => {
		try {
			if (openState) {
				if (idChecklistServicoAmbiente) {
					await loadChecklistServicoAmbienteById(idChecklistServicoAmbiente);
				} else {
					setSelectedChecklistServicoLiberacao(undefined);
					setSelectedIdChecklistServicoLiberacao(undefined);
				}
			} else {
				setSelectedChecklistServicoLiberacao(undefined);
				setSelectedIdChecklistServicoLiberacao(undefined);
			}
			setOpenCadastroModal(openState);
		} catch (e: unknown) {
			pushErrorNotification('Error', processError(e, 'Falha ao iniciar criação de novo checklist de servico de ambiente!'));
		}
	}, [
		loadChecklistServicoAmbienteById,
		setOpenCadastroModal,
		pushErrorNotification
	]);

	const handleSubmitChecklistServicoLiberacao = useCallback(async (form: ChecklistServicoAmbienteCadastroForm) => {
		if (selectedIdChecklistServico) {
			try {
				setSaving(true);
				if (selectedChecklistServicoLiberacao) {
					await updateChecklistServicoLiberacaoUseCase.execute(
						selectedChecklistServicoLiberacao.idChecklistServicoLiberacao,
						form.ordem,
						form.descricao,
					);
					pushInfoNotification("Sucesso", "Checklist de liberação atualizado com sucesso!", { isAutoFinish: true });
				} else {
					await createChecklistServicoLiberacaoUseCase.execute(
						selectedIdChecklistServico,
						form.ordem,
						form.descricao,
					);
					pushInfoNotification("Sucesso", "Checklist de liberação criado com sucesso!", { isAutoFinish: true });
					setOpenCadastroModal(false);
				}
				await loadChecklistServicoLiberacaoLista();
			} catch (e: unknown) {
				pushErrorNotification("Erro", processError(e, "Falha ao salvar checklist de liberação!"));
			} finally {
				setSaving(false);
			}
		}
	}, [
		selectedIdChecklistServico,
		selectedChecklistServicoLiberacao,
		createChecklistServicoLiberacaoUseCase,
		updateChecklistServicoLiberacaoUseCase,
		loadChecklistServicoLiberacaoLista,
		pushErrorNotification,
		pushInfoNotification
	]);

	//#endregion

	//#region PAGINAÇÃO.
	const handleReload = useCallback(async () => {
		try {
			setLoading(true);
			await loadChecklistServicoLiberacaoLista();
		} catch (e: unknown) {
			pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de servico!'));
		} finally {
			setLoading(false);
		}
	}, [
		loadChecklistServicoLiberacaoLista,
		pushErrorNotification
	]);

	const handlePageChange = useCallback(async (page: number) => {
		try {
			setLoading(true);
			pagination.setPage(page + 1);
			await loadChecklistServicoLiberacaoLista({ idChecklistServico: selectedIdChecklistServico ?? 0, currentPage: page + 1 });
		} catch (e: unknown) {
			pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de servico!'));
		} finally {
			setLoading(false);
		}
	}, [pagination, selectedIdChecklistServico, loadChecklistServicoLiberacaoLista, pushErrorNotification]);

	const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
		try {
			setLoading(true);
			pagination.setPage(1);
			pagination.setRowsPerPage(rowsPerPage);
			await loadChecklistServicoLiberacaoLista({ idChecklistServico: selectedIdChecklistServico ?? 0, rowsPerPage, currentPage: 1 });
		} catch (e: unknown) {
			pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de servico!'));
		} finally {
			setLoading(false);
		}
	}, [pagination, selectedIdChecklistServico, loadChecklistServicoLiberacaoLista, pushErrorNotification]);

	//#endregion



	//#region COPIA
	const handleChecklistServicoLiberacaoCopiaOpenState = useCallback((idChecklistServicoLiberacao: number | null, openState: boolean) => {
		if (openState) {
			if (checklistServicoLiberacaoLista && idChecklistServicoLiberacao) {
				setSelectedIdChecklistServicoLiberacao(idChecklistServicoLiberacao);
				const checklistServico = checklistServicoLiberacaoLista.find((item) => item.idChecklistServicoLiberacao === idChecklistServicoLiberacao);
				setSelectedChecklistServicoLiberacao(checklistServico);
				setOpenCopiaModal(true);
			} else {
				setSelectedIdChecklistServicoLiberacao(undefined);
				setSelectedChecklistServicoLiberacao(undefined);
				setOpenCopiaModal(false);
			}
		}
		setOpenCopiaModal(openState);
	}, [
		checklistServicoLiberacaoLista,
	]);

	const handleSubmitChecklistServicoLiberacaoCopia = useCallback(async (form: ChecklistServicoAmbienteCopiaForm) => {
		try {
			setSaving(true);
			if (selectedIdChecklistServico) {
				await createChecklistServicoLiberacaoUseCase.execute(
					selectedIdChecklistServico,
					form.ordem,
					form.descricao,
				);
				pushInfoNotification("Sucesso", " Ambiente do Checklist de servico copiado com sucesso!", { isAutoFinish: true });
			} else {
				pushErrorNotification("Erro", "Não foi possivel copiar o Ambiente do Checklist de servico!", { isAutoFinish: true });
			}
			await loadChecklistServicoLiberacaoLista();
		} catch (e: unknown) {
			pushErrorNotification("Erro", processError(e, "Falha ao Copiar Ambiente do Checklist de servico!"));
		} finally {
			setSaving(false);
			setOpenCopiaModal(false);
			setSelectedIdChecklistServicoLiberacao(undefined)
		}
	}, [
		selectedIdChecklistServico,
		createChecklistServicoLiberacaoUseCase,
		loadChecklistServicoLiberacaoLista,
		pushErrorNotification,
		pushInfoNotification
	]);

	//#endregion

	//#region DELECAO 
	const handleChecklistServicoLiberacaoDeleteOpenState = useCallback((idChecklistServicoAmbiente: number | null, openState: boolean) => {
		if (openState) {
			if (idChecklistServicoAmbiente) {
				setSelectedIdChecklistServicoLiberacao(idChecklistServicoAmbiente);
				setOpenDeleteModal(true);
			} else {
				setSelectedIdChecklistServicoLiberacao(undefined);
				setOpenDeleteModal(false);
			}
		}
		setOpenDeleteModal(openState);
	}, []);

	const handleConfirmarDeletarChecklistServicoLiberacao = useCallback(async () => {
		try {
			setSaving(true);
			if (selectedIdChecklistServicoLiberacao) {
				await deleteChecklistServicoLiberacaoUseCase.execute(selectedIdChecklistServicoLiberacao);
				pushInfoNotification("Sucesso", " Checklist deletado com sucesso!", { isAutoFinish: true });
			} else {
				pushErrorNotification("Erro", "Não foi possivel deletar Checklist!", { isAutoFinish: true });
			}
			await loadChecklistServicoLiberacaoLista()
		} catch (e: unknown) {
			pushErrorNotification("Erro", processError(e, "Falha ao Deletar Checklist!"));
		} finally {
			setSaving(false);
			setOpenDeleteModal(false);
			setSelectedIdChecklistServicoLiberacao(undefined)
		}
	}, [
		selectedIdChecklistServicoLiberacao,
		deleteChecklistServicoLiberacaoUseCase,
		loadChecklistServicoLiberacaoLista,
		pushErrorNotification,
		pushInfoNotification
	]);

	//#endregion

	//#region PESQUISA.
	const handleDescricaoChange = useDebouncedCallback(debouncedRef, (newValue: string | null) => {
		filters.setDescricao(newValue ?? undefined);
		return async () => {
			try {
				setLoading(true);
				pagination.setPage(1);
				await loadChecklistServicoLiberacaoLista({ idChecklistServico: selectedIdChecklistServico ?? 0, descricao: newValue ?? undefined, currentPage: 1 });
			} catch (e: unknown) {
				pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
			} finally {
				setLoading(false);
			}
		};
	}, [
		filters,
		pagination,
		selectedIdChecklistServico,
		loadChecklistServicoLiberacaoLista,
		pushErrorNotification
	]);

	const handleClearFilters = useCallback(async () => {
		try {
			setLoading(true);
			filters.clearFilters();
			pagination.setPage(1);
			await loadChecklistServicoLiberacaoLista({
				idChecklistServico: selectedIdChecklistServico ?? 0,
				descricao: '',
				currentPage: 1
			});
		} catch (e: unknown) {
			pushErrorNotification("Erro", processError(e, 'Falha ao buscar Ambientes do checklist de servico!'));
		} finally {
			setLoading(false);
		}
	}, [
		filters,
		selectedIdChecklistServico,
		pagination,
		loadChecklistServicoLiberacaoLista,
		pushErrorNotification
	]);

	//#endregion 

	//#region History
	const handleOpenChangeHistory = useCallback((
		idTabela: number | null,
		tipoTabela: LogTipoTabela | null,
	) => {
		if (idTabela && tipoTabela) {
			changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
		} else {
			if (selectedChecklistServicoLiberacao) {
				changeHistory.handleOpenChangeHistory(
					selectedChecklistServicoLiberacao.idChecklistServicoLiberacao,
					LogTipoTabela.CHECKLIST_SERVICO_LIBERACAO,
					true
				);
			}
		}
	}, [
		selectedChecklistServicoLiberacao,
		changeHistory,
	]);

	//#endregion

	return {
		loading,
		saving,
		checklistServicoLiberacaoLista: checklistServicoLiberacaoLista ?? [],
		errorMessage,
		filters,
		pagination,
		openDeleteModal,
		selectedIdChecklistServicoLiberacao,
		openCopiaModal,
		openCadastroModal,
		selectedChecklistServicoLiberacao,
		changeHistory,
		selectedIdChecklistServico,
		selectedChecklistServico,
		handleBack,
		handleOpenChangeHistory,
		handleSubmitChecklistServicoLiberacao,
		handleChecklistServicoLiberacaoOpenState,
		handleChecklistServicoLiberacaoCopiaOpenState,
		handleSubmitChecklistServicoLiberacaoCopia,
		handleChecklistServicoLiberacaoDeleteOpenState,
		handleConfirmarDeletarChecklistServicoLiberacao,
		handleDescricaoChange,
		handleReload,
		handlePageChange,
		handleRowsPerPageChange,
		handleClearFilters,
	};
}


